import React, { useContext } from "react";
import { sortBy } from "src/services/collections";
import { DEFAULT_BOOK_ID } from "src/common-services";
import { printPlus } from "src/services/DisplayService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import AnalyticsService from "src/services/AnalyticsService";
import { BookImage } from "src/components/BookImage";
import { STATE_MAP, WebSportItem } from "src/constants";
import { PERIODS } from "src/services/EventService";
import styles from "./style.scss";
import { MetaBetLink } from "src/services/OfferService";
import { getSportscubeFallbackLine, mergeLineWithFallback } from "./sportscubeFallback";

const EMPTY_VALUE = "--";

const hasLineForType = (line: any, lineType: string, favoriteLine = "moneyline") => {
  const period = Object.values(PERIODS).find((_period) => lineType.includes(_period)) || "FG";
  const betType = lineType === "combined" ? favoriteLine : lineType.replace(period, "");

  const spread = line?.awaySpread || line?.homeSpread;
  const moneyline = line?.awayMoney || line?.homeMoney;
  const total = line?.total || line?.overPrice;

  if (betType === "spread") {
    return spread === 0 ? true : Boolean(spread);
  }
  if (betType === "moneyline") {
    return Boolean(moneyline);
  }
  if (betType === "total") {
    return Boolean(total);
  }
  if (lineType === "combined") {
    return moneyline === 0 || spread === 0 ? true : Boolean(spread || moneyline);
  }

  return false;
};

export const orderBooks = (
  books: any,
  lineType: string,
  options?: { sportscubeOdds?: unknown; favoriteLine?: string }
) => {
  let items = [...books];
  const favoriteLine = options?.favoriteLine || "moneyline";
  const period = Object.values(PERIODS).find((_period) => lineType.includes(_period)) || "FG";

  items = sortBy(items, [
    // Consensus always first
    (book) => (book.name === "Consensus" ? 0 : 1),
    // If line is empty, move it to the end
    (book) => {
      const line = book.lines.find((_line: any) => _line.period === period) ?? {};
      const fallbackLine = getSportscubeFallbackLine({
        lineType,
        favoriteLine,
        book,
        sportscubeOdds: options?.sportscubeOdds,
      });
      const mergedLine = mergeLineWithFallback(line, fallbackLine);
      const exists = hasLineForType(mergedLine, lineType, favoriteLine);

      return exists ? 0 : 1;
    },
    // Allow sort via Retool
    (book) => book.rank,
  ]);

  return items;
};

interface GameLinesRowProps {
  game: { [key: string]: any } | null;
  lineType: string;
  currentSportObj: WebSportItem | null;
  books: [];
  sportscubeOdds?: unknown;
  loading: boolean;
  size: any;
}

/*
 * The row of sportsbook lines that are displayed in the lines component on the game detail page.
 * This component does not use state as the user cannot interact with this component it is only
 * for displaying components and links.  This could be made into a functional component instead of
 * a class but things were broken out into methods and classes are nice.
 */
const GameLinesRow = (props: GameLinesRowProps) => {
  const context = useContext(SubscriptionContext);
  const { lineType, currentSportObj, game, loading, size, sportscubeOdds } = props;
  const isCombined = lineType === "combined";
  const isTotal = lineType.indexOf("total") !== -1;
  const isSpread = lineType.indexOf("spread") !== -1;
  const isMoney = lineType.indexOf("money") !== -1;

  /*
   * A single block displaying the information for a sportsbook's line.
   * This may be a Line Element but it looks like it is a sportsbook entry for the user so
   * it was named that way.  Functionally it takes a single line and produces as JSX
   * component to display to the user.
   *
   * The component displays four lines of data:
   * - Sportsbook name or logo if available
   * - a line that corresponds to the away team row but might not be the data for the away team, for
   *   example if total is chosen
   * - a similar line corresponding to the home team row
   * - A bet now button if there is an affiliate link and the user is in New Jersey
   *
   * @param line the line to display the data for
   */
  const sportbookEl = (book: any) => {
    const period = Object.values(PERIODS).find((_period) => lineType.includes(_period)) || "FG";
    const baseLine = book.lines.find((l: any) => l.lineType === "current" && l.period === period) ?? {};
    const fallbackLine = getSportscubeFallbackLine({
      lineType,
      favoriteLine: currentSportObj?.favoriteLine,
      book,
      sportscubeOdds,
    });
    const line = mergeLineWithFallback(baseLine, fallbackLine);

    /*
     * Compute the data for the away and home rows based on the line type selected.
     * We utilize the precomputed entries in the game object from the display service where we can.
     */
    let awayLine: string = EMPTY_VALUE,
      homeLine: string = EMPTY_VALUE,
      drawLine: string = EMPTY_VALUE;

    if (isTotal) {
      if (["NHL", "MLB"].includes(currentSportObj?.key ?? "")) {
        awayLine = line.total ? `o${line.total} (${line.overPrice || EMPTY_VALUE})` : "--";
        homeLine = line.total ? `u${line.total} (${line.underPrice || EMPTY_VALUE})` : "--";
      } else {
        awayLine = line.total ? `o${line.total}` : "--";
        homeLine = `(${line.overPrice || EMPTY_VALUE})`;
      }
    } else if (isCombined) {
      let betType = "awaySpread";
      if (currentSportObj?.favoriteLine === "moneyline") {
        betType = "awayMoney";
      }
      if (line[betType] === 0) {
        awayLine = "Even";
      } else {
        awayLine = printPlus(line[betType]);
      }
      awayLine = `${awayLine} (${printPlus(line.awayPrice)})`;
      homeLine = line.total ? `o${line.total} (${printPlus(line.overPrice)})` : `${printPlus(line.overPrice)}`;
    } else if (isSpread) {
      if (line.awaySpread === 0 && line.homeSpread === 0) {
        awayLine = "Even";
        homeLine = "Even";
      } else {
        awayLine = printPlus(line.awaySpread);
        homeLine = printPlus(line.homeSpread);
      }
      awayLine = `${awayLine} (${printPlus(line.awayPrice)})`;
      homeLine = `${homeLine} (${printPlus(line.homePrice)})`;
    } else if (isMoney) {
      awayLine = `${printPlus(line.awayMoney)}`;
      homeLine = `${printPlus(line.homeMoney)}`;
      drawLine = `${printPlus(line.drawMoney)}`;
    }

    const pageLocation = window?.location?.href ?? "";

    const noLines = awayLine.includes("--") && homeLine.includes("--") && drawLine.includes("--");
    const showBetNowLink = STATE_MAP[context.userLocale]?.legal && !noLines && book.id !== DEFAULT_BOOK_ID;

    return (
      <div className={`sportbook-con ${size}`} key={book.id}>
        <div className="sportbook-logo" style={{ height: "32px" }}>
          <BookImage book={book} style={{ height: "32px" }} />
        </div>
        <div className="sportbook-text">{awayLine}</div>
        <div className="sportbook-text">{homeLine}</div>
        {currentSportObj?.hasDraws && isMoney ? <div className="sportbook-text">{drawLine}</div> : null}
        {showBetNowLink && (
          <MetaBetLink
            className="bet-now-button"
            href={book.prismicOffers?.[0]?.web_link}
            target="_blank"
            rel="noindex nofollow noopener"
            onClick={() =>
              AnalyticsService.track("Bet Now", {
                page: pageLocation.includes("game-predictions") ? "Game Detail" : "Games Table",
                url: pageLocation,
                sport: currentSportObj?.name,
                book: book.name,
                state: context.userLocale,
                component: "GameLines",
                lineType,
              })
            }
            bookName={book.name}
            sportsdataGameId={game?.sportsdataGameId}
          >
            BET NOW
          </MetaBetLink>
        )}
      </div>
    );
  };

  const placeholderSportbookEl = () => (
    <div className={`sportbook-con ${size}`} key={Math.random()}>
      <div className="sportbook-logo placeholder" style={{ height: 32, width: 120 }}>
        &nbsp;
      </div>
      <div className="sportbook-text placeholder" style={{ width: 50 }}>
        &nbsp;
      </div>
      <div className="sportbook-text placeholder" style={{ width: 50 }}>
        &nbsp;
      </div>
      {currentSportObj?.hasDraws && isMoney ? (
        <div className="sportbook-text placeholder" style={{ width: 50 }}>
          &nbsp;
        </div>
      ) : null}
      <div className="bet-now-button placeholder" style={{ width: 90 }} />
    </div>
  );

  /*
   * The element that contains all sportsbook entries.
   * This is a horizontal list that can become a scroll box if there are too many
   * sportsbooks to display.  Its main purpose is to filter the lines from the game
   * provided to a list that should be displayed to the user.
   */
  const lineScrollList = () => {
    const { books } = props;
    const items = orderBooks(books, lineType, {
      sportscubeOdds,
      favoriteLine: currentSportObj?.favoriteLine,
    });
    // let items: any[] = [...books];

    return (
      <div className={`lines-list-con ${size}`}>
        {!loading && items.length
          ? items.map((item: any) => sportbookEl(item))
          : [1, 2, 3, 4, 5, 6, 7].map(placeholderSportbookEl)}
      </div>
    );
  };

  /*
   * The leftmost element on the lines row is considered a legend where the names
   * of the teams are placed and this is fixed on the left and does not scroll even
   * if there are many sportsbooks and they do scroll.
   */
  const linesTeamLegend = (
    { game: { homeTeam, awayTeam } } = {
      game: {
        homeTeam: {
          fullName: "",
          lastName: "",
        },
        awayTeam: { fullName: "", lastName: "" },
      },
    }
  ) => {
    return (
      <div className="lines-legend-con">
        <div className="team-legend-header-text">{currentSportObj?.isTennis ? "Player" : "Team"}</div>
        <div className="team-legend-team-text" style={{ visibility: isTotal ? "hidden" : "visible" }}>
          {currentSportObj?.key === "CFB" || currentSportObj?.key === "CBK" ? awayTeam.fullName : awayTeam.lastName}
        </div>
        <div className="team-legend-team-text" style={{ visibility: isTotal ? "hidden" : "visible" }}>
          {currentSportObj?.key === "CFB" || currentSportObj?.key === "CBK" ? homeTeam.fullName : homeTeam.lastName}
        </div>
        {currentSportObj?.hasDraws && isMoney ? <div className="team-legend-team-text">Draw</div> : null}
      </div>
    );
  };

  return (
    <div className={`lines-con ${size}`}>
      {!loading && currentSportObj && linesTeamLegend({ game: game as any })}
      {lineScrollList()}
      <style jsx global>
        {styles}
      </style>
    </div>
  );
};

export default GameLinesRow;
